import {
  ApiProgressLoader,
  DateTimeFormatter,
  EventEmitter,
  Row,
  getTranslatedText
} from '@nile/components/src';
import { SVGIcon } from '@superops/super-icons';
import { Avatar, Button, Icon, TextStyle } from '@superops/ui-design-system';
import React, { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import AppointmentViewEditContainer from './AppointmentViewEditContainer';
import SchedulingContext from '../Contexts/SchedulingContext';
import { Tooltip } from 'react-clone-tippy';
import { FormattedMessage, useIntl } from 'react-intl';
import { AVAILABLE_CALENDARS } from '../helpers';
import { useMutation } from '@apollo/client';
import { RESYNC_APPOINTMENT, TicketClient } from '@nile/graphql/src';
import AppointmentEditFloaterPortal from './AppointmentEditFloater';
import { PSA_CONSTANTS } from '@nile/components/src/Constants';
import { RenderTooltip } from '@nile/components/src/Utils/helpers';

const ICON_MAP = {
  TICKET: 'TicketFilled',
  PROJECT: 'ProjectFilled'
};

const SYNC_STATUS_MAP = {
  QUEUE: 'IN_QUEUE',
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
  DELETE_IN_PROGRESS: 'DELETION_IN_PROGRESS'
};
const AppointmentEvent = (props) => {
  const intl = useIntl();
  const start = props?.event?.start;
  const end = props?.event?.end;
  const timespent = dayjs(end).diff(dayjs(start)) / (1000 * 60 * 60);
  const schedulingContext = useContext(SchedulingContext);
  const currentView = schedulingContext?.calendarView;
  const syncStatus = props?.event?.appointment?.syncStatus;
  const isPrivateEvent =
    props.event?.appointment?.sensitivity ===
    PSA_CONSTANTS.SCHEDULING_APPOINTMENT_TYPE.PRIVATE;
  const isNoActionPrivateEvent =
    isPrivateEvent && !props.event?.appointment?.attendees?.length;

  const [appointmentTippyVisible, setAppointmentTippyVisible] = useState(false);

  const appointmentTippyRef = React.useRef(null);

  const isSmallEvent = timespent < 0.75; // Time less than 1h

  const popupParentName = 'appointmentPopup';

  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    if (
      syncStatus === SYNC_STATUS_MAP.QUEUE ||
      syncStatus === SYNC_STATUS_MAP.DELETE_IN_PROGRESS
    ) {
      setShowLoader(true);
    } else {
      setTimeout(() => {
        setShowLoader(false);
      }, 2000);
    }
  }, [syncStatus]);

  const closeAllOpenPopups = () => {
    if (
      document.getElementsByClassName(popupParentName) &&
      document.getElementsByClassName(popupParentName)?.length
    ) {
      Array.prototype.forEach.call(
        document.getElementsByClassName(popupParentName),
        (popup) => {
          if (popup?._tippy?.state?.isVisible) {
            popup?._tippy.hide();
          }
        }
      );
    }
  };

  const isDeleteInProgress = syncStatus === SYNC_STATUS_MAP.DELETE_IN_PROGRESS;
  const syncFailure = syncStatus === SYNC_STATUS_MAP.FAILURE;
  const currentCalendar = AVAILABLE_CALENDARS.find(
    (el) => el?.integrationId === schedulingContext?.integratedCalendarName
  );
  const calendarIcon = currentCalendar?.SVGIcon || 'Outlook';
  let appointmentSource = props?.event?.appointment?.appointmentSource || null;
  if (appointmentSource) {
    appointmentSource =
      appointmentSource?.charAt(0).toUpperCase() +
      appointmentSource.slice(1).toLowerCase();
  }
  const hideWorkItem = syncFailure && isSmallEvent;

  const togglePopOver = () => {
    // if (!isDeleteInProgress) {
    //   if (!appointmentTippyRef.current._tippy.state?.isVisible) {
    //     closeAllOpenPopups();
    //     setAppointmentTippyVisible(true);
    //     appointmentTippyRef.current._tippy.show();
    //   } else if (appointmentTippyRef.current._tippy.state?.isVisible) {
    //     appointmentTippyRef.current._tippy.hide();
    //     setAppointmentTippyVisible(false);
    //   }
    // }
    if (isNoActionPrivateEvent) return;
    setAppointmentTippyVisible(!appointmentTippyVisible);
  };

  const workItemInput =
    props?.event?.workItem?.workId && props?.event?.workItem?.module
      ? {
          workId: props?.event?.workItem?.workId,
          module: props?.event?.workItem?.module
        }
      : null;

  const [resyncAppointmentEvent, { loading: resyncLoading }] = useMutation(
    RESYNC_APPOINTMENT,
    {
      client: TicketClient,
      onCompleted() {
        EventEmitter.dispatch('refreshCalendar');
      }
    }
  );

  const resyncAppointment = (e) => {
    e.stopPropagation();
    // if (appointmentTippyRef.current._tippy.state?.isVisible) {
    //   appointmentTippyRef.current._tippy.hide();
    //   setAppointmentTippyVisible(false);
    // }
    const appointmentId = props?.event?.appointment?.appointmentId;
    if (appointmentId) {
      resyncAppointmentEvent({
        variables: {
          input: {
            appointmentId: appointmentId
          }
        }
      });
    }
  };

  const errorReason = props?.event?.appointment?.syncErrorReason || null;

  return (
    <>
      {appointmentTippyVisible && (
        <AppointmentEditFloaterPortal
          eventId={props?.event?.eventId}
          togglePopOver={togglePopOver}
          workItem={workItemInput}
          integratedDate={new Date(schedulingContext?.integratedDate)}
          schedulingContext={schedulingContext}
          isPrivateEvent={isPrivateEvent}
        />
      )}
      <RenderTooltip
        isVisible={isNoActionPrivateEvent}
        tooltip={
          <Tooltip
            distance={35}
            title={getTranslatedText(
              intl,
              'ticket.scheduling.privateEvent.noAction'
            )}
            position="top-start"
            inertia
            trigger="mouseenter"
            animation="shift"
            animateFill={false}
          />
        }
      >
        <div
          className={`appointment-event ${popupParentName} ${isNoActionPrivateEvent &&
            'cursor-not-allowed'}`}
          id="appointment-event-component"
          onClick={!props?.event?.disableEvents && togglePopOver} // If disable events is passed click event wont take place
        >
          {isSmallEvent ? (
            <div className="flex fx-sb actr">
              <div className="flex g10 ellipsis">
                <p className="ellipsis">
                  <Row gap={5}>
                    {isPrivateEvent && <SVGIcon name="Lock" />}
                    <TextStyle weight="Medium">
                      {props?.event?.appointment?.subject}
                    </TextStyle>
                  </Row>
                </p>
                {syncFailure && currentView === 'week' ? (
                  <></>
                ) : (
                  <div>
                    <div className="flex white-space-no-wrap g-3 f-12">
                      <TextStyle weight="Medium">
                        <DateTimeFormatter
                          type="time"
                          date={props?.event?.start}
                        />
                      </TextStyle>
                      <div> - </div>
                      <div>
                        <TextStyle weight="Medium">
                          <DateTimeFormatter
                            type="time"
                            date={props?.event?.end}
                          />
                        </TextStyle>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {syncFailure && (
                <div
                  className={`sync-error-container flex fx-sb g10 actr position-relative ${
                    showLoader ? 'loading' : ''
                  }`}
                >
                  <Tooltip
                    title={errorReason}
                    position="bottom-start"
                    inertia
                    trigger="mouseenter"
                    animation="shift"
                    animateFill={false}
                    arrow
                  >
                    <div className="flex g5 actr pointer">
                      <div>
                        <SVGIcon name="ErrorRound" />
                      </div>
                    </div>
                  </Tooltip>
                  <div>
                    <Button
                      icon={<SVGIcon name={calendarIcon} size="small" />}
                      onClick={(e) => resyncAppointment(e)}
                      size="slim"
                      loading={resyncLoading}
                    >
                      <div className="f-12">
                        <FormattedMessage id="common.reSync" />
                      </div>
                    </Button>
                  </div>
                </div>
              )}

              {props?.event?.showUser && (
                <div className="event-source mb-10 appointment-users">
                  {props?.event?.appointment?.attendees?.map((attendee) => {
                    if (attendee?.user?.userId) {
                      return (
                        <div
                          key={attendee?.user?.userId}
                          className="appointment-attendee"
                        >
                          <Tooltip
                            title={attendee?.user?.name}
                            position="bottom-start"
                            inertia
                            trigger="mouseenter"
                            animation="shift"
                            animateFill={false}
                            theme="light"
                            arrow
                          >
                            <Avatar
                              size="extraSmall"
                              initials={attendee?.user?.name?.[0]}
                            />
                          </Tooltip>
                        </div>
                      );
                    }
                  })}
                </div>
              )}

              {props?.event?.appointment?.recurringEvent && (
                <div className="recurring-icon">
                  <Tooltip
                    title={getTranslatedText(intl, 'common.recurring.event')}
                    position="bottom-start"
                    inertia
                    trigger="mouseenter"
                    animation="shift"
                    animateFill={false}
                    arrow
                  >
                    <SVGIcon name="Recurring" />
                  </Tooltip>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="mb5 ellipsis lh-18">
                <Row gap={5}>
                  {isPrivateEvent && <SVGIcon name="Lock" />}
                  <TextStyle weight="Medium">
                    {props?.event?.appointment?.subject}
                  </TextStyle>
                </Row>
              </div>

              <div className="flex g5 f-12 lh-18">
                <div className="flex g5">
                  <div>
                    <TextStyle weight="Medium">
                      <DateTimeFormatter
                        type="time"
                        date={props?.event?.start}
                      />
                    </TextStyle>
                  </div>
                  -
                  <div>
                    <TextStyle weight="Medium">
                      <DateTimeFormatter type="time" date={props?.event?.end} />
                    </TextStyle>
                  </div>
                </div>
              </div>

              {props?.event?.workItem && !hideWorkItem && (
                <div className="flex g5 mt-10 actr">
                  <div>
                    <SVGIcon
                      name={ICON_MAP?.[props?.event?.workItem?.module]}
                    />
                  </div>
                  <div className="ellipsis f-12 lh-18">
                    <TextStyle weight="Medium">
                      {props?.event?.workItem?.subject}
                    </TextStyle>
                  </div>
                </div>
              )}

              {(props?.event?.appointment?.appointmentSource !== 'INTERNAL' ||
                props?.event?.showUser) && (
                <div className="event-source">
                  {props?.event?.showUser && (
                    <div className="mb-10 appointment-users">
                      {props?.event?.appointment?.attendees?.map((attendee) => {
                        if (attendee?.user?.userId) {
                          return (
                            <div
                              key={attendee?.user?.userId}
                              className="pointer appointment-attendee"
                            >
                              <Tooltip
                                title={attendee?.user?.name}
                                position="bottom-start"
                                inertia
                                trigger="mouseenter"
                                animation="shift"
                                animateFill={false}
                                theme="light"
                                arrow
                              >
                                <Avatar
                                  size="extraSmall"
                                  initials={attendee?.user?.name?.[0]}
                                />
                              </Tooltip>
                            </div>
                          );
                        }
                      })}
                    </div>
                  )}

                  {props?.event?.appointment?.appointmentSource !==
                    'INTERNAL' && (
                    <div className="ellipsis">
                      <TextStyle variation="subdued">
                        <span className="f-12">
                          <FormattedMessage id="scheduling.event.source.text" />
                        </span>
                        <span className="capitalize ml-5 f-12 italic">
                          {appointmentSource}
                        </span>
                      </TextStyle>
                    </div>
                  )}
                </div>
              )}

              {props?.event?.appointment?.recurringEvent && (
                <div className="recurring-icon">
                  <Tooltip
                    title={getTranslatedText(intl, 'common.recurring.event')}
                    position="bottom-start"
                    inertia
                    trigger="mouseenter"
                    animation="shift"
                    animateFill={false}
                    arrow
                  >
                    <SVGIcon name="Recurring" />
                  </Tooltip>
                </div>
              )}

              {syncFailure && (
                <div
                  className={`sync-error-container flex fx-sb g10 actr ${
                    showLoader ? 'loading' : ''
                  }`}
                >
                  <Tooltip
                    title={errorReason}
                    position="bottom-start"
                    inertia
                    trigger="mouseenter"
                    animation="shift"
                    animateFill={false}
                    arrow
                  >
                    <div className="flex g5 actr pointer">
                      <div>
                        <SVGIcon name="ErrorRound" />
                      </div>
                      <div>
                        <TextStyle weight="Medium">
                          <FormattedMessage id="common.error" />
                        </TextStyle>
                      </div>
                    </div>
                  </Tooltip>
                  <div>
                    <Button
                      icon={<SVGIcon name={calendarIcon} size="small" />}
                      onClick={(e) => resyncAppointment(e)}
                      loading={resyncLoading}
                    >
                      <div className="f-12">
                        <FormattedMessage id="common.reSync" />
                      </div>
                    </Button>
                  </div>
                </div>
              )}

              {showLoader && (
                <>
                  <div className="sync-loader">
                    {syncStatus === SYNC_STATUS_MAP.QUEUE && (
                      <ApiProgressLoader isLoading />
                    )}
                    {syncStatus === SYNC_STATUS_MAP.DELETE_IN_PROGRESS && (
                      <ApiProgressLoader isLoading />
                    )}
                    {syncStatus === SYNC_STATUS_MAP.SUCCESS && (
                      <ApiProgressLoader isSuccess />
                    )}
                    {syncStatus === SYNC_STATUS_MAP.FAILURE && (
                      <ApiProgressLoader isSuccess={false} />
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </RenderTooltip>
    </>
  );
};

export default AppointmentEvent;
